import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataService } from '../services/dataService';
import { User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (resource: keyof User['permissions'], action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const token = localStorage.getItem('dealercentives_auth_token');
      if (token) {
        const user = await dataService.getCurrentUser();
        if (user) {
          setState({
            user,
            isAuthenticated: true,
            isLoading: false
          });
        } else {
          // Invalid token, clear it
          localStorage.removeItem('dealercentives_auth_token');
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Session check failed:', error);
      localStorage.removeItem('dealercentives_auth_token');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { token, user } = await dataService.login(email, password);
      localStorage.setItem('dealercentives_auth_token', token);
      setState({
        user,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await dataService.logout();
      localStorage.removeItem('dealercentives_auth_token');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear the state even if the API call fails
      localStorage.removeItem('dealercentives_auth_token');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    }
  };

  const hasPermission = (resource: keyof User['permissions'], action: string): boolean => {
    if (!state.user) return false;
    if (state.user.role === 'super_admin') return true;
    if (state.user.role === 'admin') return true;
    return state.user.permissions[resource]?.includes(action) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        login,
        logout,
        hasPermission
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};